import { Component, type ErrorInfo, type ReactNode } from "react";
import { withTranslation, type WithTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

interface Props extends WithTranslation {
  children: ReactNode;
  /** Optional reset key — changing it clears the error (e.g. route path). */
  resetKey?: string | number;
}

interface State {
  error: Error | null;
}

/**
 * App-level error boundary. Catches render-time errors in the subtree so a
 * single broken screen doesn't blank the whole SPA. Async/data errors are
 * handled by TanStack Query + route error components; this is the last resort
 * for unexpected render crashes.
 */
class ErrorBoundaryInner extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface to the console; a real error-reporting sink can hook in here.
    console.error("Uncaught render error:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    const { t, children } = this.props;
    if (!error) return children;

    return (
      <div
        role="alert"
        className="mx-auto flex max-w-md flex-col items-center gap-4 py-16 text-center"
      >
        <h1 className="text-xl font-semibold">{t("errors.boundaryTitle")}</h1>
        <p className="text-muted-foreground">{t("errors.boundaryMessage")}</p>
        <Button onClick={() => this.setState({ error: null })}>
          {t("errors.retry")}
        </Button>
      </div>
    );
  }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryInner);
